"use client";

import React from "react";
import Image from "next/image";
import { PatientSelectorProps } from "./types";
import { defaultSelectorTheme, listLayoutTheme } from "./themes";
import { mergeTheme } from "../utils/theme";
import { PatientListItem } from "./patient-list-item";

export const PatientSelector: React.FC<PatientSelectorProps> = ({
  patients,
  selectedPatient,
  onSelectPatient,
  theme,
  className = "",
  layout = "grid",
  showChevron = false,
  avatarBaseUrl = "/avatars",
  selectionIndicator,
  renderAvatar,
  renderLabel,
  renderChevron,
  ariaLabel = "Select a patient",
  "data-testid": dataTestId,
}) => {
  // Use different base theme depending on layout
  const baseTheme = layout === "list" ? listLayoutTheme : defaultSelectorTheme;
  const mergedTheme = mergeTheme(baseTheme, theme);

  // For list layout, use the PatientListItem component
  if (layout === "list") {
    return (
      <div
        className={`${mergedTheme.container} ${className}`.trim()}
        role="radiogroup"
        aria-label={ariaLabel}
        data-testid={dataTestId}
      >
        {patients.map((patient) => {
          const isSelected = selectedPatient?.id === patient.id;

          return (
            <PatientListItem
              key={patient.id}
              patient={patient}
              isSelected={isSelected}
              onClick={() => onSelectPatient(patient)}
              theme={{
                container: mergedTheme.patientItem,
                avatar: mergedTheme.avatar?.image,
                label: mergedTheme.label,
                chevron: mergedTheme.chevron,
                selected: {
                  container: mergedTheme.selected?.item,
                  label: mergedTheme.selected?.label,
                  chevron: mergedTheme.selected?.chevron,
                },
              }}
              showChevron={showChevron}
              avatarBaseUrl={avatarBaseUrl}
              renderAvatar={renderAvatar}
              renderLabel={
                renderLabel
                  ? (patient, isSelected) => renderLabel(patient, isSelected)
                  : undefined
              }
              renderChevron={
                renderChevron
                  ? (isSelected) => renderChevron(patient, isSelected)
                  : undefined
              }
            />
          );
        })}
      </div>
    );
  }

  // Grid layout (original implementation)
  const renderSelectionIndicator = (isSelected: boolean) => {
    if (typeof selectionIndicator === "function") {
      return selectionIndicator(isSelected);
    }
    if (selectionIndicator) {
      return selectionIndicator;
    }
    // Default selection indicator for grid
    const selectionClass = [
      mergedTheme.avatar?.selection,
      isSelected
        ? mergedTheme.selected?.selection
        : mergedTheme.unselected?.selection,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={selectionClass}
      >
        <circle
          cx="24"
          cy="24"
          r="23"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    );
  };

  const renderAvatarContent = (patient: any, isSelected: boolean) => {
    if (renderAvatar) {
      return renderAvatar(patient, isSelected);
    }
    // Default avatar rendering
    const imageSrc = patient.profileImage
      ? `${avatarBaseUrl}/${patient.profileImage}.svg`
      : `${avatarBaseUrl}/default.png`;

    return (
      <Image
        src={imageSrc}
        alt={`${patient.firstName} avatar`}
        width={48}
        height={48}
        className={mergedTheme.avatar?.image}
      />
    );
  };

  const renderLabelContent = (patient: any, isSelected: boolean) => {
    if (renderLabel) {
      return renderLabel(patient, isSelected);
    }
    // Default label rendering
    const labelClass = [
      mergedTheme.label,
      isSelected ? mergedTheme.selected?.label : mergedTheme.unselected?.label,
    ]
      .filter(Boolean)
      .join(" ");

    return <span className={labelClass}>{patient.firstName}</span>;
  };

  return (
    <div
      className={`${mergedTheme.container} ${className}`.trim()}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {patients.map((patient) => {
        const isSelected = selectedPatient?.id === patient.id;

        return (
          <label
            key={patient.id}
            className={mergedTheme.patientItem}
            data-testid={`patient-selector-item-${patient.id}`}
          >
            <input
              type="radio"
              name="selectedPatientId"
              value={patient.id}
              checked={isSelected}
              onChange={() => onSelectPatient(patient)}
              className="sr-only"
              aria-label={`Select ${patient.firstName}`}
            />
            <div className={mergedTheme.avatar?.container}>
              {renderSelectionIndicator(isSelected)}
              {renderAvatarContent(patient, isSelected)}
            </div>
            {renderLabelContent(patient, isSelected)}
          </label>
        );
      })}
    </div>
  );
};

PatientSelector.displayName = "PatientSelector";
